import { useMemo } from "react";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { ClaimModuleKey } from "@/constants/claim-options";

/**
 * Shared tab bar for the claim detail center pane.
 *
 * The center pane renders one full-height panel per tab (Claim Activity,
 * TurboAI, Notes, Email). This tab bar is rendered once at the top of the
 * center pane above each panel header so navigation stays fixed while panel
 * content and headers change below it. Keeping the triggers here is the single
 * source of truth: adding or renaming a tab happens in exactly one place.
 */
const TAB_TRIGGER_CLASS =
    "w-full py-2 px-3 rounded-md data-[state=active]:bg-primary/10 data-[state=active]:text-primary font-medium text-xs text-center sm:flex-1 sm:px-4 sm:py-1.5";

const TABS = [
    { value: "activity", label: "Claim Activity", moduleKey: null },
    { value: "turboai", label: "TurboAI", moduleKey: null },
    { value: "notes", label: "Notes", moduleKey: 'notes' },
    { value: "email", label: "Email", moduleKey: 'emails' },
] as const satisfies ReadonlyArray<{
    value: string;
    label: string;
    moduleKey: ClaimModuleKey | null;
}>;

interface ClaimDetailTabsListProps {
    hiddenModules?: ClaimModuleKey[];
}

export function ClaimDetailTabsList({ hiddenModules = [] }: ClaimDetailTabsListProps) {
    const visibleTabs = useMemo(
        () =>
            TABS.filter((tab) => {
                if (!tab.moduleKey) return true;
                return !hiddenModules.includes(tab.moduleKey);
            }),
        [hiddenModules],
    );

    return (
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 rounded-lg border bg-white/50 p-1 shadow-xs sm:flex sm:gap-0 sm:p-0.5 dark:bg-slate-900/50">
            {visibleTabs.map(({ value, label }) => (
                <TabsTrigger key={value} value={value} className={TAB_TRIGGER_CLASS}>
                    {label}
                </TabsTrigger>
            ))}
        </TabsList>
    );
}
